import { createContext, type ReactNode, useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import {
  type FieldValues,
  type Path,
  type PathValue,
  type DeepPartial,
  type UseFormGetValues,
  type UseFormSetValue,
  type UseFormWatch,
  type FieldPathValue,
} from 'react-hook-form';
import { z } from 'zod';
import { type UseQueryResult } from '@tanstack/react-query';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import useGetEnrichedInfo, {
  enrichResponseSchema,
  type EnrichResponseType,
} from './hooks/useGetEnrichedInfo';
import { StyledWrapper, StyledFormContent, StyledSpinnerWrapper } from './FormEnrichment.styles';

const DEBOUNCE_TIME = 500;
const validateEmail = (email?: string) => {
  const value = z.string().email().safeParse(email);

  return value.success ? value.data : undefined;
};

const getFieldNames = <T extends FieldValues>(
  fields: T,
  ...ommitedFields: Array<Path<T> | string>
) => {
  return Object.keys(fields).filter((field): field is Path<T> => !ommitedFields.includes(field));
};

export const FormEnrichmentContext = createContext<Array<string>>([]);

interface FormEnrichmentProps<T extends FieldValues> {
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  enrichEmailFieldName: Path<T>;
  getValues: UseFormGetValues<T>;
  defaultValues?: DeepPartial<{ [key: string]: any }>;
  mapping: Partial<{
    [key in Exclude<Path<T>, keyof EnrichResponseType>]:
      | ((_data: EnrichResponseType) => FieldPathValue<T, key> | DeepPartial<T>[key])
      | keyof EnrichResponseType;
  }>;

  render: (enrichmentStatus: UseQueryResult['status']) => ReactNode;
}

const FormEnrichment = <T extends FieldValues>({
  watch,
  mapping,
  setValue,
  enrichEmailFieldName,
  defaultValues,
  getValues,
  render,
}: FormEnrichmentProps<T>) => {
  const mappingRef = useRef(mapping);
  const emailFieldValue = watch(enrichEmailFieldName);
  const email = useDebounce(validateEmail(emailFieldValue), DEBOUNCE_TIME);
  const [visibleFields, setVisibleFields] = useState<Array<Path<T>>>([enrichEmailFieldName]);

  const { status, data, fetchStatus } = useGetEnrichedInfo(email);
  const isLoading = fetchStatus === 'fetching';

  /**
   * If an error occured in enrich endpoint (either no match or a server error),
   * simply show all the fields
   */
  useEffect(() => {
    if (status === 'error') {
      getFieldNames(getValues(), enrichEmailFieldName).forEach((field) => {
        const isFieldEnrichable =
          field in mappingRef.current || field in enrichResponseSchema.shape;

        if (isFieldEnrichable) {
          setValue(field, defaultValues?.[field]);
        }

        setVisibleFields((prev) => {
          return [...new Set([field, ...prev])];
        });
      });
    }
  }, [defaultValues, enrichEmailFieldName, getValues, setValue, status]);

  /**
   * When a response is received from our enrich endpoint,
   * Fill all the form fields that are available, and show all the non-filled fields
   */
  useEffect(() => {
    if (status === 'success') {
      getFieldNames(getValues(), enrichEmailFieldName).forEach((field) => {
        const isFieldEnrichable =
          field in mappingRef.current || field in enrichResponseSchema.shape;

        const mappedFieldName =
          field in mappingRef.current
            ? mappingRef.current[field as keyof typeof mappingRef.current]
            : field;

        const value =
          typeof mappedFieldName === 'function'
            ? mappedFieldName(data)
            : data[mappedFieldName as keyof EnrichResponseType];

        if (isFieldEnrichable) {
          setValue(field, value as PathValue<T, typeof field>);
        }

        setVisibleFields((visibleFields) => {
          if (isFieldEnrichable && value) {
            return visibleFields.filter((visibleField) => visibleField !== field);
          }

          return [...new Set([field, ...visibleFields])];
        });
      });
    }
  }, [status, data, setValue, getValues, defaultValues, enrichEmailFieldName]);

  return (
    <FormEnrichmentContext.Provider value={visibleFields}>
      <StyledWrapper>
        {isLoading && (
          <StyledSpinnerWrapper role="alert" aria-busy="true">
            <LoadingSpinner />
          </StyledSpinnerWrapper>
        )}
        <StyledFormContent isLoading={isLoading}>{render(status)}</StyledFormContent>
      </StyledWrapper>
    </FormEnrichmentContext.Provider>
  );
};

export default FormEnrichment;
